import React, { useState, useEffect, useRef } from "react";
import useForm from './hooks/useForm';
import { FormField, LoadingIcon } from '@prosolve/common-components-ui'
import useFetch from './hooks/useFetch';
import { useNavigate } from "react-router-dom";
import { getClientSubdomain } from './utils';
// import SsoButtons from "./sso/SsoButtons"; // SSO login not supported yet — see commented block below
import useSnackbar from "./hooks/useSnackbar";
import StudentLoginPage from "./StudentLoginPage";
import {
  Page,
  MazeOverlay,
  Card,
  BrandLogo,
  Head,
  Title,
  Subtitle,
  FieldWrap,
  fieldLabelStyle,
  LinkRow,
  TextLink,
  ErrorSlot,
  SubmitRow,
  SubmitButton,
  SpinnerWrap,
  // Divider, // needed by the disabled SSO block below
  Foot,
  Tagline,
  CrossLink
} from './sharedComponents/AuthPageLayout';

export default () => {
  const url = new URL(window.location);
  const { searchParams } = url;
  const ssoError = searchParams.get("ssoError");
  const studentParam = searchParams.get('student');
  const isStudent = studentParam === 'true'

  if (isStudent) {
    return <StudentLoginPage />
  }

  const snackbar = useSnackbar()

  useEffect(() => {
    if (ssoError === 'true') {
      snackbar.newMessage({
        message: 'Error validating SSO Credentials',
        severity: 'failure'
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoError])

  const navigate = useNavigate();

  const clientSubdomain = getClientSubdomain()

  const isStarSubdomain = (clientSubdomain === 'star')

  const fetch = useFetch();

  const [isBusy, setIsBusy] = useState(false);
  const [showInvalidLogin, setShowInvalidLogin] = useState(false);

  const formSchema = {
    requiredFields: ['email', 'password'],
    initData: {
      email: '',
      password: ''
    }
  }

  const formData = useForm(formSchema)

  // Block below is necessary for password managers that auto fill inputs which never trigger components 'onChange' events
  const wrapperElRef = useRef()
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (wrapperElRef.current) {
        const inputs = wrapperElRef.current.getElementsByTagName('input')
        const userInput = inputs[0]
        const passwordInput = inputs[1]
        formData.setValues({ ...formData.values, email: (userInput?.value || ''), password: (passwordInput?.value || '') })
      }
    });

    if (wrapperElRef.current) {
      const inputs = wrapperElRef.current.getElementsByTagName('input')
      const userInput = inputs[0]
      if (userInput) {
        observer.observe(userInput, { attributes: true, childList: true, subtree: true });
      }
      const passwordInput = inputs[1]
      if (passwordInput) {
        observer.observe(passwordInput, { attributes: true, childList: true, subtree: true });
      }
    }

    return () => observer.disconnect()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickSubmit = async () => {
    if (formData.isValid()) {
      setIsBusy(true)

      const response = await fetch(
        `auth/login`,
        {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formData.values)
        }
      )

      const jsonData = await response.json() || {};

      const { status = '' } = jsonData;

      if (status) {
        window.location.reload()
      } else {
        setShowInvalidLogin(true)
      }

      setIsBusy(false)
    }
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      onClickSubmit();
    }
  }

  const onStudentLoginClicked = () => {
    const baseUrl = window.location.origin
    const replaceUrl = `${baseUrl}?student=true`
    window.location.replace(replaceUrl);
    // window.location.assign(replaceUrl); // keeps history
  }

  return (
    <Page $isStar={isStarSubdomain}>

      { !isStarSubdomain && <MazeOverlay aria-hidden="true" /> }

      <Card>

        { !isStarSubdomain && <BrandLogo /> }

        <Head>
          <Title>{'Welcome back'}</Title>
          <Subtitle>
            { isStarSubdomain
              ? 'Sign in to your account to continue.'
              : 'Sign in to your ProSolve Hub account to continue.'
            }
          </Subtitle>
        </Head>

        <div ref={wrapperElRef}>

          <FieldWrap $invalid={!!formData.missingValues['email']}>
            <FormField
              label='Email'
              labelStyle={fieldLabelStyle}
              fieldId='email'
              type='text'
              placeholder='you@district.org'
              formData={formData}
              onKeyDown={onKeyDown}
              isLoginField={true}
            />
          </FieldWrap>

          <FieldWrap $invalid={!!formData.missingValues['password']}>
            <FormField
              label='Password'
              labelStyle={fieldLabelStyle}
              fieldId='password'
              type='password'
              placeholder='Enter your password'
              formData={formData}
              onKeyDown={onKeyDown}
              isLoginField={true}
              hasPasswordToggle={true}
            />
          </FieldWrap>

        </div>

        <LinkRow>
          <TextLink onClick={() => navigate('forgotpassword')}>
            {'Forgot password?'}
          </TextLink>
        </LinkRow>

        <ErrorSlot>
          { (!isBusy && showInvalidLogin) ? 'Unable to login' : '' }
        </ErrorSlot>

        <SubmitRow>
          <SubmitButton onClick={onClickSubmit}>
            {'Sign In'}
          </SubmitButton>
          {
            isBusy &&
            <SpinnerWrap>
              <LoadingIcon />
            </SpinnerWrap>
          }
        </SubmitRow>

        {/* SSO login is not supported yet — re-enable this block (and the SsoButtons + Divider imports above) when ready */}
        {/* <Divider>{'or continue with'}</Divider>

        <SsoButtons /> */}

        { !isStarSubdomain &&
          <Foot>
            <Tagline>{'The Human Skills Company'}</Tagline>
            <CrossLink>
              <TextLink onClick={onStudentLoginClicked} style={{ fontSize: '14px' }}>
                {'Student? Click here to log in'}
              </TextLink>
            </CrossLink>
          </Foot>
        }

      </Card>
    </Page>
  )
}
